// src/web/client/src/pages/Landing.jsx - Complete Fixed Version
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  ArrowRight,
  BarChart3,
  MessageSquare,
  Sparkles,
  Store,
  Users,
  Zap,
  Check,
  AlertCircle,
  ExternalLink,
  Loader2
} from "lucide-react";

export default function Landing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isEmbedded, setIsEmbedded] = useState(false);
  const [shopDomain, setShopDomain] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if embedded in Shopify
    const embedded = window.top !== window.self;
    setIsEmbedded(embedded);
    
    // Get shop from URL params
    const shop = searchParams.get('shop');
    if (shop) {
      setShopDomain(shop);
      localStorage.setItem('shop_domain', shop);
      localStorage.setItem('shopDomain', shop);
    }
    
    // Check if already authenticated
    const token = localStorage.getItem('token');
    const storedShop = localStorage.getItem('shop_domain');
    
    if (token && storedShop && token !== 'demo-token') {
      setIsAuthenticated(true);
      // If authenticated and embedded, go to dashboard
      if (embedded) {
        navigate('/dashboard');
      }
    }
  }, [navigate, searchParams]);
  
  const features = [
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "AI-Powered Messages",
      description: "Generate personalized messages that resonate with each customer"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Instant Setup",
      description: "Connect your Shopify store and start sending in minutes"
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Real-Time Analytics",
      description: "Track opens, clicks, and conversions with detailed insights"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Customer Segmentation",
      description: "Target the right message to the right customer at the right time"
    }
  ];

  // ⚠️ KNOWN DUPLICATION. Prices and limits here are a hand-maintained copy of
  // src/backend/config/plans.js (the single source of truth) — the frontend
  // has no plan-catalogue endpoint to read them from. If you change a price or
  // an allowance, change it in ALL FOUR places: config/plans.js, this page,
  // pages/Billing.jsx, and the published Shopify App Store listing (which
  // lives outside this repo and only a human can update).
  const pricingPlans = [
    {
      name: "Basic",
      price: "$29.99",
      period: "/month",
      features: [
        "Up to 300 orders / month",
        "Up to 1,000 messages / month",
        "Thank-you & review emails",
        "Analytics dashboard",
        "Email support"
      ],
      cta: "Start 90-Day Free Trial",
      highlighted: false
    },
    {
      name: "Pro",
      price: "$49.99",
      period: "/month",
      features: [
        "Up to 1,500 orders / month",
        "Unlimited messages",
        "Advanced AI personalization",
        "VIP identification",
        "Customer segmentation",
        "Priority support"
      ],
      cta: "Start 90-Day Free Trial",
      highlighted: true
    },
    {
      name: "Scale",
      price: "$99.99",
      period: "/month",
      features: [
        "Up to 5,000 orders / month",
        "Everything in Pro, included",
        "Unlimited messages",
        "Advanced AI personalization",
        "Priority support"
      ],
      cta: "Start 90-Day Free Trial",
      highlighted: false
    },
    {
      name: "Platform",
      price: "$199.99",
      period: "/month",
      features: [
        "Up to 10,000 orders / month",
        "Everything in Scale, included",
        "Customer value, churn risk & lifecycle stage",
        "Priority support"
      ],
      cta: "Start Free Trial",
      highlighted: false,
      // HIDDEN until brain-driven sending is live (phase 4). The public
      // pricing page must not advertise a tier whose headline promise is not
      // delivered yet; the in-app Billing card describes only what works
      // today. Flip this off in the phase-4 PR.
      hidden: true
    }
  ];

  const handleGetStarted = () => {
    if (isEmbedded && shopDomain) {
      // If embedded with shop domain, start OAuth
      handleShopifyAuth();
    } else {
      // Otherwise go to login
      navigate('/login');
    }
  };

  const handleShopifyAuth = () => {
    if (!shopDomain) {
      navigate('/onboarding');
      return;
    }

    setLoading(true);
    const authUrl = `https://api.ecomoria.com/auth?shop=${shopDomain}`;
    
    if (isEmbedded) {
      // Open in popup for embedded apps
      const width = 600;
      const height = 700;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;
      
      const authWindow = window.open(
        authUrl,
        'shopify-auth',
        `width=${width},height=${height},left=${left},top=${top}`
      );
      
      if (!authWindow) {
        // Popup blocked - fallback to redirect
        window.location.href = authUrl;
        return;
      }
      
      // Listen for completion
      const checkInterval = setInterval(() => {
        try {
          if (authWindow.closed) {
            clearInterval(checkInterval);
            setLoading(false);
            
            // Check if now authenticated
            const token = localStorage.getItem('token');
            if (token && token !== 'demo-token') {
              navigate('/dashboard');
            }
          }
        } catch (e) {
          // Ignore cross-origin errors
        }
      }, 1000);
      
      // Also listen for postMessage
      window.addEventListener('message', function onMessage(event) {
        if (event.data.type === 'AUTH_SUCCESS') {
          clearInterval(checkInterval);
          setLoading(false);
          window.removeEventListener('message', onMessage);
          navigate('/dashboard');
        }
      });
    } else {
      // Normal redirect for non-embedded
      window.location.href = authUrl;
    }
  };

  const handleStartTrial = () => {
    navigate('/onboarding');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Embedded App Notice */}
      {isEmbedded && (
        <Alert className="m-4 border-indigo-200 bg-indigo-50">
          <AlertCircle className="h-4 w-4 text-indigo-600" />
          <AlertDescription className="text-indigo-800">
            {shopDomain ? (
              <>
                <strong>Welcome, {shopDomain}!</strong>
                {isAuthenticated ? (
                  <span> You're already connected. <Link to="/dashboard" className="underline">Go to Dashboard</Link></span>
                ) : (
                  <span> Click "Get Started" below to connect your store.</span>
                )}
              </>
            ) : (
              "Running inside Shopify Admin"
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Store className="w-8 h-8 text-indigo-600" />
              <span className="text-2xl font-bold">Ecomoria</span>
            </div>
            <div className="flex items-center space-x-6">
              <a href="#features" className="text-gray-600 hover:text-gray-900 transition-colors">
                Features
              </a>
              <a href="#pricing" className="text-gray-600 hover:text-gray-900 transition-colors">
                Pricing
              </a>
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')}
              >
                Login
              </Button>
              <Button onClick={handleGetStarted} disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    Get Started
                    {isEmbedded && <ExternalLink className="w-4 h-4 ml-2" />}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="bg-gradient-to-b from-indigo-50 to-white">
        <div className="container mx-auto px-6 py-20">
          <div className="text-center max-w-4xl mx-auto">
            <Badge className="mb-4" variant="secondary">
              🚀 Built for Shopify DTC brands
            </Badge>
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Turn Every Order Into a
              <span className="text-indigo-600"> Lasting Relationship</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Boost customer loyalty with AI-powered post-purchase messages.
              No coding required.
            </p>
            <div className="flex gap-4 justify-center">
              <Link to="/onboarding">
                <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700">
                  Start 90-Day Free Trial
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
             {/* HIDDEN for Shopify Review
              <Button size="lg" variant="outline">
                Watch Demo
              </Button>
              */}
            </div>
            <p className="mt-4 text-sm text-gray-500">
              No credit card required • Setup in 2 minutes
            </p>
          </div>
        </div>
      </div>

      {/* Value Props (replaces fabricated stats bar — pre-launch honesty) */}
      <div className="border-y bg-gray-50 py-8">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap justify-center items-center gap-10 md:gap-16">
            <div className="text-center flex flex-col items-center">
              <Sparkles className="w-8 h-8 text-indigo-600 mb-2" />
              <div className="font-semibold text-gray-900">AI-Powered</div>
            </div>
            <div className="text-center flex flex-col items-center">
              <MessageSquare className="w-8 h-8 text-indigo-600 mb-2" />
              <div className="font-semibold text-gray-900">Personalized</div>
            </div>
            <div className="text-center flex flex-col items-center">
              <Zap className="w-8 h-8 text-indigo-600 mb-2" />
              <div className="font-semibold text-gray-900">Automated</div>
            </div>
            <div className="text-center flex flex-col items-center">
              <Store className="w-8 h-8 text-indigo-600 mb-2" />
              <div className="font-semibold text-gray-900">Shopify-Native</div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div id="features" className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Everything You Need to Grow
            </h2>
            <p className="text-xl text-gray-600">
              Powerful features that work together seamlessly
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-4 text-indigo-600">
                    {feature.icon}
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="bg-gray-50 py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600">
              Get started in minutes, see results in days
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-indigo-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="font-semibold text-lg mb-2">Connect Your Store</h3>
              <p className="text-gray-600">
                One-click Shopify integration. No developers needed.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-indigo-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="font-semibold text-lg mb-2">Customize Messages</h3>
              <p className="text-gray-600">
                Choose templates or let AI create perfect messages.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-indigo-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="font-semibold text-lg mb-2">Watch Sales Grow</h3>
              <p className="text-gray-600">
                Track results and optimize with real-time analytics.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials section removed pre-launch — will return once design
          partners give us real quotes. */}

      {/* Pricing */}
      <div id="pricing" className="bg-gray-50 py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600">
              90-day free trial on every plan. Cancel anytime.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {pricingPlans.filter((plan) => !plan.hidden).map((plan, index) => (
              <Card 
                key={index} 
                className={`relative ${plan.highlighted ? 'ring-2 ring-indigo-600 shadow-lg scale-105' : ''}`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-indigo-600">Most Popular</Badge>
                  </div>
                )}
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                  <div className="mb-6">
                    <span className="text-4xl font-bold">{plan.price}</span>
                    <span className="text-gray-600">{plan.period}</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className="w-full" 
                    variant={plan.highlighted ? "default" : "outline"}
                    onClick={plan.cta === "Contact Sales" ? undefined : handleGetStarted}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      plan.cta
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-indigo-600 py-20">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Grow Your Business?
          </h2>
          <p className="text-xl text-indigo-100 mb-8">
            Be one of the first stores to grow with Ecomoria.
          </p>
          <Button 
            size="lg" 
            variant="secondary"
            onClick={handleStartTrial}
          >
            Start Your Free Trial
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Footer
          Every link in here must point at something real. Pre-launch, this
          page goes in front of Shopify reviewers and prospective design
          partners — a dead link reads as "this product isn't done." The
          previous version had 11 dead `href="#"` links (Integrations, API,
          About, Blog, Careers, Contact, Help Center, Documentation, Status,
          Terms — plus a missing Privacy Policy). They've all been removed
          or pointed at a real destination:
            - Features / Pricing  → in-page anchors that exist below
            - Contact             → mailto: the live founder address Help.jsx uses
            - Privacy Policy      → /privacy (route registered in pages/index.jsx,
                                    required for the Shopify App Store listing)
          If you add a new link here, it must be reachable today, not "we'll
          build it later." Better to ship a smaller footer than a fake one. */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Store className="w-6 h-6" />
                <span className="text-xl font-bold">Ecomoria</span>
              </div>
              <p className="text-gray-400">
                Boost customer loyalty with AI-powered post-purchase engagement.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#features" className="hover:text-white">Features</a></li>
                <li><a href="#pricing" className="hover:text-white">Pricing</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <a href="mailto:founder@ecomoria.com" className="hover:text-white">
                    founder@ecomoria.com
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  {/* Plain <a>, not <Link>, so the link works identically
                      whether the user is on a static-rendered Render snapshot
                      or inside React Router — /privacy is a registered route
                      in pages/index.jsx and a full navigation is fine. */}
                  <a href="/privacy" className="hover:text-white">Privacy Policy</a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2026 Ecomoria. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}